import {
	Alert,
	Box,
	Button,
	Container,
	Link,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryState } from "nuqs";
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// Define custom error messages for common authentication errors
const USER_FRIENDLY_ERRORS = {
	"No refresh token available": "Invalid username or password. Please try again.",
	"Invalid credentials": "The username or password you entered is incorrect.",
	"Login failed": "Login failed. Please check your credentials and try again.",
	"Token refresh failed": "Your session has expired. Please log in again.",
	"Network Error":
		"Unable to connect to the server. Please check your internet connection.",
	"Authentication failed": "Authentication failed. Please check your credentials.",
	"Account is inactive or awaiting approval":
		"Your account is not yet activated. Please contact an administrator for approval.",
	"Account awaiting admin approval":
		"Your account is waiting for administrator approval. You'll receive an email when your account is activated.",
	"Account has been deactivated":
		"Your account has been deactivated. Please contact an administrator for assistance.",
};

// Ensure these are visible in browser console
// console.log("[LoginForm] Available error mappings:", USER_FRIENDLY_ERRORS);

// Fixed logical error in condition that was causing problems
const isInactiveAccountError = (msg: string) => {
	return (
		msg.includes("inactive") ||
		msg.includes("awaiting approval") ||
		msg.includes("Account awaiting admin approval") ||
		msg.includes("Account has been deactivated")
	);
};

export const LoginForm: React.FC = () => {
	const [usernameOrEmail, setUsernameOrEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const { login, isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const [next] = useQueryState("next");
	// Only honour internal paths to avoid open-redirects.
	const redirectTo =
		next && next.startsWith("/") && !next.startsWith("//") ? next : "/";

	// Redirect to the intended destination once authenticated
	useEffect(() => {
		if (isAuthenticated) {
			navigate(redirectTo, { replace: true });
		}
	}, [isAuthenticated, navigate, redirectTo]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// Basic validation - just check if field is empty
		if (!usernameOrEmail.trim()) {
			setError("Please enter your username or email");
			return;
		}

		if (!password) {
			setError("Please enter your password");
			return;
		}

		try {
			// Pass the input value as is - could be username or email
			await login(usernameOrEmail, password);
		} catch (err) {
			// DEBUG: Log the raw error object to understand its structure
			console.warn("[LoginForm] LOGIN ERROR CAUGHT:", err);

			// Try to safely stringify the error object to see all properties
			let errorString;
			try {
				errorString = JSON.stringify(err, Object.getOwnPropertyNames(err), 2);
			} catch (stringifyError) {
				errorString = `[Error cannot be stringified: ${stringifyError}]`;
			}
			console.warn("[LoginForm] ERROR STRING:", errorString);

			// Extract error message, handling both Error objects and API error responses
			let errorMessage = "An error occurred";
			let errorSource = "unknown";

			if (err instanceof Error) {
				errorMessage = err.message;
				errorSource = "Error.message";
				console.warn(
					"[LoginForm] Error message from Error.message:",
					errorMessage
				);

				// Some errors might have additional response data
				const anyError = err as any;
				console.warn(
					"[LoginForm] Does error have response?",
					!!anyError.response
				);

				// If error has a response property with data
				if (anyError.response) {
					console.warn(
						"[LoginForm] Error response object:",
						anyError.response
					);
					const responseData = anyError.response.data;

					// Check different possible locations for the error message
					if (responseData) {
						// Log the full response data object
						console.warn("[LoginForm] Full response data:", responseData);

						if (responseData.error) {
							errorMessage = responseData.error;
							errorSource = "response.data.error";
							console.warn(
								"[LoginForm] Found error in response.data.error:",
								errorMessage
							);
						} else if (responseData.detail) {
							errorMessage = responseData.detail;
							errorSource = "response.data.detail";
							console.warn(
								"[LoginForm] Found error in response.data.detail:",
								errorMessage
							);
						} else {
							// If we can't find a specific error field, stringify the whole response
							console.warn(
								"[LoginForm] No specific error field found in response data"
							);
							try {
								errorMessage = JSON.stringify(responseData);
								errorSource = "stringified response.data";
							} catch (e) {
								// If JSON stringify fails, just use the default message
							}
						}
					}
				}
			}

			// Before mapping, check what exactly our error is for debugging
			console.warn("[LoginForm] Final extracted error message:", errorMessage);
			console.warn(
				"[LoginForm] Checking if message contains 'inactive' or 'awaiting':",
				isInactiveAccountError(errorMessage)
			);

			// Check if error is in our mapping - IMPORTANT for debugging
			const hasMapping = USER_FRIENDLY_ERRORS.hasOwnProperty(errorMessage);
			console.warn(
				"[LoginForm] Is error in USER_FRIENDLY_ERRORS mapping?",
				hasMapping,
				"Key:",
				JSON.stringify(errorMessage)
			);

			// If we have no mapping but should for this type of error, check for partial matches
			if (!hasMapping && isInactiveAccountError(errorMessage)) {
				console.warn(
					"[LoginForm] This appears to be an inactive account error but wasn't matched exactly"
				);
				// Force use of the inactive account message
				errorMessage = "Account is inactive or awaiting approval";
				console.warn("[LoginForm] Forced error message to:", errorMessage);
			}

			// Map technical error messages to user-friendly ones
			const userFriendlyMessage =
				USER_FRIENDLY_ERRORS[errorMessage] || errorMessage;

			console.warn("[LoginForm] Error handling details:", {
				errorSource,
				originalMessage: errorMessage,
				inMapping: hasMapping,
				mappedMessage: userFriendlyMessage,
				allMappings: Object.keys(USER_FRIENDLY_ERRORS),
			});

			setError(userFriendlyMessage);
		}
	};

	return (
		<Container component="main" maxWidth="xs">
			<Paper elevation={3} sx={{ p: 4, mt: 8 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<Typography component="h1" variant="h5">
						Sign in
					</Typography>
					<Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<TextField
							margin="normal"
							required
							fullWidth
							id="usernameOrEmail"
							label="Username or Email"
							name="usernameOrEmail"
							autoComplete="username email"
							autoFocus
							value={usernameOrEmail}
							onChange={(e) => setUsernameOrEmail(e.target.value)}
						/>
						<TextField
							margin="normal"
							required
							fullWidth
							name="password"
							label="Password"
							type="password"
							id="password"
							autoComplete="current-password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							sx={{ mt: 3, mb: 2 }}
						>
							Sign In
						</Button>
						<Box sx={{ textAlign: "center" }}>
							<Link component={RouterLink} to="/register" variant="body2">
								Don't have an account? Register
							</Link>
						</Box>
						<Box
							sx={{
								textAlign: "center",
								mt: 3,
								pt: 2,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							<Link
								component={RouterLink}
								to="/privacy-policy"
								variant="body2"
								sx={{
									mr: 3,
									color: "text.secondary",
									textDecoration: "none",
									"&:hover": {
										color: "primary.main",
										textDecoration: "underline",
									},
								}}
							>
								Privacy Policy
							</Link>
							<Link
								component={RouterLink}
								to="/terms-of-service"
								variant="body2"
								sx={{
									color: "text.secondary",
									textDecoration: "none",
									"&:hover": {
										color: "primary.main",
										textDecoration: "underline",
									},
								}}
							>
								Terms of Service
							</Link>
						</Box>
					</Box>
				</Box>
			</Paper>
		</Container>
	);
};
